import { PLAYERS_ROOM_UID, REPLAY_ROOM_UID, UTILITY_ROOM_UID, worldToLocalTransform, isVector3, isQuaternion, UNIT_Y } from "flavr-common";
import { Matrix4, Quaternion, Vector3 } from "flavr-math";
import { modelPool } from "./ModelPool";
/**
 * The model facet clone context provides information about the original and cloned model hierarchies as well as the
 * model hierarchy of the whole world. It is used when cloning facets to update UIDs that might have referenced models
 * in the original model hierarchy to now reflect models in the cloned hierarchy
 */
export class ModelFacetCloneContext {
    constructor(originalModel, clonedModel) {
        this.originalModel = originalModel;
        this.clonedModel = clonedModel;
        this.uidMapping = new Map();
        // Create 1-to-1 mappings between original and cloned models
        const originalModels = [originalModel];
        const clonedModels = [clonedModel];
        while (originalModels.length > 0) {
            if (originalModels.length !== clonedModels.length)
                throw new Error("Number of original and cloned descendants don't match, this should not happen");
            const original = originalModels.shift();
            const clone = clonedModels.shift();
            if (original === undefined || clone === undefined)
                break;
            // Add mapping between the original model and the cloned model
            this.uidMapping.set(original.getUID(), clone.getUID());
            this.uidMapping.set(clone.getUID(), original.getUID());
            // Add children to queue
            originalModels.push(...original.getChildren());
            clonedModels.push(...clone.getChildren());
        }
    }
    /// General
    /**
     * True when both uids reference the same model
     */
    equals(uidA, uidB) {
        const modelA = modelPool.getModel(uidA);
        const modelB = modelPool.getModel(uidB);
        if (modelA === undefined || modelB === undefined)
            return false;
        return modelA.equals(modelB);
    }
    /**
     * True when both models exist and the first model is the parent of the second
     */
    isParentOf(parentUID, childUID) {
        const parent = modelPool.getModel(parentUID);
        if (parent === undefined)
            return false;
        return parent.getChild(childUID) !== undefined;
    }
    /**
     * True when both models exist and the first model is the child of the second
     */
    isChildOf(childUID, parentUID) {
        const parent = modelPool.getModel(parentUID);
        if (parent === undefined)
            return false;
        return parent.getChild(childUID) !== undefined;
    }
    /**
     * True when both models exist and the first model is the descendant of the second
     */
    isDescendantOf(descendantUID, ancestorUID) {
        const descendant = modelPool.getModel(descendantUID);
        const ancestor = modelPool.getModel(ancestorUID);
        if (ancestor === undefined || descendant === undefined)
            return false;
        return ancestor.isAncestorOf(descendant);
    }
    /**
     * True when both models exist and the first model is the ancestor of the second
     */
    isAncestorOf(ancestorUID, descendantUID) {
        const ancestor = modelPool.getModel(ancestorUID);
        const descendant = modelPool.getModel(descendantUID);
        if (ancestor === undefined || descendant === undefined)
            return false;
        return ancestor.isAncestorOf(descendant);
    }
    /**
     * Finds the equivalent descendant in the destination hierarchy. If a model in the original model hierarchy is
     * specified then the returned model uid will be a model in the clone hierarchy and vice versa. If the input model is
     * not part of either hierarchies then undefined is returned.
     *
     * Example:
     * ```
     *     original                clone
     *        A                      U
     *       / \                    / \
     *      B   C                  V   W
     *     / \  |                 / \  |
     *    D  E  F                X  Y  Z
     * ```
     * So if we would call `findEquivalentDescendant(E)` then the result would be `Y`.
     */
    findEquivalentDescendant(sourceDescendantUID) {
        return this.uidMapping.get(sourceDescendantUID);
    }
}
/**
 * Shorthand function that create or uses the provided BroadcastProps and adds `externalMessages: messages` to the props.
 * This is useful when we don't want to send the messages internally but instead gather all messages that are created by
 * model or modelPool and send these messages by ourselves
 */
export const useExternalMessages = (messages, props) => {
    return Object.assign(Object.assign({}, props), { externalMessages: messages });
};
/**
 * Returns true if the model is part of the scene. All models that are not part of the replay room and are not the
 * root model, are part of the scene
 * @param model
 */
export function isModelPartOfScene(model) {
    const replayRoom = modelPool.getRoomOrThrow(REPLAY_ROOM_UID);
    return (model.isRoom() || model.getRoomDepth() > 0) && !model.isDescendantOfOrEquals(replayRoom);
}
/**
 * Returns true for rooms that exist for engine/session bookkeeping rather than for the active layout world.
 */
export function isNonSessionRoom(room) {
    if (room === undefined || !room.isRoom())
        return false;
    switch (room.getUID()) {
        case PLAYERS_ROOM_UID:
        case REPLAY_ROOM_UID:
        case UTILITY_ROOM_UID:
            return true;
        default:
            return false;
    }
}
/**
 * Computes a new local matrix that is relative to the parent of the provided model. If the model is the root model
 * the matrix will be returned as is.
 */
export function computeLocalMatrixFromWorldMatrix(model, newWorldMatrix, result = new Matrix4()) {
    return (model.hasParent())
        ? worldToLocalTransform(newWorldMatrix, model.getParentOrThrow().getWorldTransform(), result)
        : result.copy(newWorldMatrix);
}
/**
 * Computes a new local matrix that is relative to the parent of the provided model. If the model is the root model
 * the matrix will be returned as is. If any of the transform components are not provided, then the previous world
 * transform components are being used instead
 */
export function computeLocalMatrixFromWorldTransformComponents(model, newWorldPosition, newWorldOrientation, newWorldScale) {
    const oldWorldTransformComponents = model.getWorldTransformComponents();
    newWorldPosition = (newWorldPosition === undefined)
        ? oldWorldTransformComponents.position
        : (isVector3(newWorldPosition)) ? newWorldPosition : new Vector3().fromArray(newWorldPosition);
    newWorldOrientation = (newWorldOrientation === undefined)
        ? oldWorldTransformComponents.orientation
        : (isQuaternion(newWorldOrientation)) ? newWorldOrientation : new Quaternion().fromArray(newWorldOrientation);
    newWorldScale = (newWorldScale === undefined)
        ? oldWorldTransformComponents.scale
        : (isVector3(newWorldScale)) ? newWorldScale : new Vector3().fromArray(newWorldScale);
    const newWorldMatrix = new Matrix4();
    newWorldMatrix.compose(newWorldPosition, newWorldOrientation, newWorldScale);
    return computeLocalMatrixFromWorldMatrix(model, newWorldMatrix);
}
export const modelLookAt = (model, targetWorldPosition) => {
    // This method does not support objects having non-uniformly-scaled parent(s)
    targetWorldPosition = isVector3(targetWorldPosition) ? targetWorldPosition : new Vector3(targetWorldPosition[0], targetWorldPosition[1], targetWorldPosition[2]);
    const position = model.getWorldPosition();
    const matrix = new Matrix4().lookAt(position, targetWorldPosition, UNIT_Y);
    const quaternion = new Quaternion().setFromRotationMatrix(matrix);
    const localTransform = computeLocalMatrixFromWorldTransformComponents(model, undefined, quaternion, undefined);
    model.setTransform(localTransform);
};
